import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../data/slice/authSlice";
import { useChangePasswordMutation } from "../../../feature-module/peoples/centers/users/api/usersApi";
import { showToast } from "../../utils/alert";

interface ChangePasswordModalProps {
  show: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ show, onClose }) => {
  const currentUser = useSelector(selectCurrentUser);
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!show) return null;

  const resetForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setErrorMessage(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!currentUser?.id) {
      setErrorMessage("User session not found. Please log in again.");
      return;
    }

    if (!oldPassword.trim()) {
      setErrorMessage("Please enter your current password.");
      return;
    }

    if (!newPassword.trim()) {
      setErrorMessage("Please enter a new password.");
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword === oldPassword) {
      setErrorMessage("New password cannot be the same as your current password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("New password and confirm password do not match.");
      return;
    }

    try {
      await changePassword({
        id: currentUser.id,
        payload: {
          old_password: oldPassword,
          new_password: newPassword,
        },
      }).unwrap();

      showToast({
        title: "Success",
        text: "Password changed successfully!",
        icon: "success",
      });

      handleClose();
    } catch (err: any) {
      const serverMessage =
        err?.data?.message ||
        err?.data?.error?.error ||
        err?.data?.error ||
        "Failed to change password. Please verify your current password.";
      setErrorMessage(typeof serverMessage === "string" ? serverMessage : "Failed to change password.");
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1061 }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isLoading) {
            handleClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow border-0">
            <div className="modal-header border-bottom">
              <h5 className="modal-title d-flex align-items-center">
                <i className="ti ti-lock me-2 text-primary fs-20"></i>
                Change Password
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                disabled={isLoading}
                onClick={handleClose}
              ></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4">
                {errorMessage && (
                  <div className="alert alert-danger alert-dismissible fade show d-flex align-items-center mb-3 py-2 px-3">
                    <i className="ti ti-alert-triangle me-2 fs-18"></i>
                    <div className="small">{errorMessage}</div>
                  </div>
                )}

                {/* Current Password */}
                <div className="mb-3">
                  <label className="form-label fw-medium">
                    Current Password <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="ti ti-key text-muted"></i>
                    </span>
                    <input
                      type={showOldPassword ? "text" : "password"}
                      className="form-control border-start-0 border-end-0"
                      placeholder="Enter current password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      disabled={isLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-light border border-start-0 text-muted"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      tabIndex={-1}
                    >
                      <i className={`ti ${showOldPassword ? "ti-eye-off" : "ti-eye"}`}></i>
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="mb-3">
                  <label className="form-label fw-medium">
                    New Password <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="ti ti-lock text-muted"></i>
                    </span>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="form-control border-start-0 border-end-0"
                      placeholder="Enter new password (min. 6 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-light border border-start-0 text-muted"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex={-1}
                    >
                      <i className={`ti ${showNewPassword ? "ti-eye-off" : "ti-eye"}`}></i>
                    </button>
                  </div>
                  <small className="text-muted fs-11">
                    Must be at least 6 characters long
                  </small>
                </div>

                {/* Confirm Password */}
                <div className="mb-2">
                  <label className="form-label fw-medium">
                    Confirm New Password <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="ti ti-lock-check text-muted"></i>
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control border-start-0 border-end-0"
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-light border border-start-0 text-muted"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      <i className={`ti ${showConfirmPassword ? "ti-eye-off" : "ti-eye"}`}></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-top bg-light py-2 px-4">
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm d-flex align-items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-check me-1"></i>
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordModal;

